import axios from 'axios'
import type { CheckoutCustomizations, TravelRuleFields, TravelRuleFieldsCollected } from '../components/Checkout'
import { API_BASE_URL } from '../util/Constants'

export interface IProduct {
  id: string
  name: string
  description: string
  chargingIntervalSeconds: number
  currency: string
  price: number
  createdAt: Date
  imageUrl: string
  updatedAt: Date
  sellerAddress: string
  productType?: {
    Presale?: {
      Token?: {
        decimals?: any
        ticker?: any
      }
    }
  }
  nextBillingDateAt?: string
}

export interface IPaymentMethod {
  network: string
  token?: string
}

export enum InputFieldDataType {
  String = 'String',
  Number = 'Number',
  Email = 'Email',
  Address = 'Address',
  CountryOfResidence = 'CountryOfResidence',
  Date = 'Date',
  Name = 'String'
}

export interface InputField {
  dataType: InputFieldDataType
  inputLabel: string
  isRequired: boolean
}

export type InputFieldWithValue = InputField & { value: string | number | boolean | Date }

export interface IManagedPaymentMethod {
  network: string
  token?: string
  discountPercentOff?: number
}

export interface IManagedGateway {
  methods: IManagedPaymentMethod[]
}

export interface KycData {
  name: string
  dob: string
  address: string
  phone: string
  selfieUrl?: string
  passportUrl?: string
}

export interface OpenBankingGateway {
  email: string
  kycData: KycData
  customerId: string
  openBankingCheckoutUrl: string
  residenceCountry: string
  destinationCryptoAddress?: string
  /**
   * What the payment adds to the balance in USDC: an estimate before the
   * payment, the settled amount after it. Absent while there is no price to
   * estimate from.
   */
  onrampAmount?: number | string | null
  instantPaymentStatus: boolean
  existingUserPaidSuccess: boolean
}

export interface OpenBankingOrderStatusHistoryEntry {
  currentStatus: string
  reason: string
  createdAt: string
  updatedAt: string
  previousStatus?: string
}

export interface OpenBankingPaymentData {
  status: string
  paymentScheme?: string
  instantPaymentScheme?: boolean
  payerFinancialAddress?: unknown
  statusHistoryList?: OpenBankingOrderStatusHistoryEntry[]
  shopper?: {
    email?: string
    phoneNumber?: string
  }
}

export interface IPaymentLink {
  id: string
  url: string
  products: IProduct[]
  gateway: {
    managed?: IManagedGateway
  }
  customizations: CheckoutCustomizations
  sellerAddress: string
  sellerLogoUrl: string
  sellerName: string
  createdAt: Date
  updatedAt: Date
  successUrl?: string
  errorUrl?: string
  inputFields: InputField[]
  chargeCustomerNetworkFee: boolean
  presaleData?: any
  total?: number
  currency?: string
}

export interface IDiscountCode {
  id: string
  code: string
  model: {
    modelType: 'percentage' | 'fixed'
    amountOff: number
  }
  products: string[]
  createdAt: string
  updatedAt: string
}

export interface PriceQuote {
  from: string
  to: string
  value: number
  expiryTimestamp: string
}

export interface Profile {
  id: string
  email: string
  name: string
  residenceCountry?: string
  companyName?: string
  dob?: string
  billingAddress?: string
  phone?: string
  telegram?: string
  discord?: string
  logoUrl?: string
  accountType?: string
}

export interface AddressSuggestionDetails {
  formattedAddress: string
  addressLine1?: string
  addressLine2?: string
  townOrCity?: string
  stateOrProvince?: string
  postalCode?: string
  country?: string
  countryCode?: string
}

export interface CountryGuessResponse {
  countryCode?: string
  countryName?: string
  source: string
}

export type SellerProfile = Profile & { sellerAddress: string }

export interface ProductWithQuantity {
  product: IProduct
  quantity: number
}

export interface LineItem {
  invoiceId: string
  currency?: string
  name: string
  quantity: number
  price: number
}

export interface IInvoice {
  id: string
  sellerAddress: string
  seller: Profile
  gateway: {
    managed?: IManagedGateway
  }
  customer: Profile
  products: ProductWithQuantity[]
  lineItems: LineItem[]
  issuedAt: Date
  paidAt?: Date
  voidedAt?: Date
  overdueAt?: Date
  inputData: Array<{ key: string, value: string }>
  status: 'paid' | 'pending' | 'voided' | 'overdue'
  memo?: string
}

export type ICreateInvoice = Omit<IInvoice, 'id' | 'customer' | 'issuedAt' | 'paidAt' | 'voidedAt' | 'status'> & { 'customerIds': string[] }

export interface KeyValuePair {
  key: string
  value: string
}

export interface CheckoutItemData {
  name: string
  description?: string
  quantity: number
  chargingIntervalSeconds: number
  imageUrl?: string
  currency?: string
  price: number
  nextBillingDateAt?: string
}

export interface CheckoutSession {
  id: string
  organizationId: string
  sessionStatus: string
  sellerAddress: string
  products: IProduct[]
  items: CheckoutItemData[]
  total?: number
  currency?: string
  gateway: {
    managed?: IManagedGateway
    openBanking?: OpenBankingGateway
  }
  successUrl: string
  cancelUrl?: string
  metadata?: KeyValuePair[]
  expiresAt?: Date
  createdAt?: Date
  updatedAt?: Date
  customizations?: any
  chargeCustomerNetworkFee?: boolean
  travelRuleFields: TravelRuleFieldsCollected
  travelRuleEmail?: string
  openBankingPaymentData?: OpenBankingPaymentData
}

export interface GetCheckoutSessionOptions {
  refreshOpenBankingStatus?: boolean
}

export interface PaymentMethod {
  managed?: {
    method: IManagedPaymentMethod
  }
  managedTokenDelegate?: {
    buyerAddress?: string
    token?: string
    network: string
  }
}

export interface NewPendingPaymentLinkOrder {
  paymentLinkId: string
  paymentMethod: PaymentMethod
  orderData: KeyValuePair[]
  amount?: number
  currency?: string
  orderProductData?: any[]
  discountCode?: string
  emailAddress?: string
  travelRuleFields?: TravelRuleFields
}

export interface NewPendingDonationLinkOrder {
  donationLinkId: string
  paymentMethod: IManagedPaymentMethod
  orderData: KeyValuePair[]
  amount: number
  currency: string
}

export interface NewPendingCheckoutSessionOrder {
  checkoutSessionId: string
  paymentMethod: PaymentMethod
  orderData: KeyValuePair[]
  orderProductData: any[]
  emailAddress?: string
  discountCode?: string
}

export interface ICheckoutSessionOrder {
  id: string
  checkoutSessionId: string
  paymentMethodDetails: PaymentMethodDetails
  createdAt: Date
}

export type INetwork = 'Bitcoin' | 'Ethereum' | 'Solana' | 'SepoliaTestnet' | 'Polygon' | 'PolygonTestnet' | 'BNB' | 'BNBTestnet' | 'Base' | 'Optimism' | 'OpBnb' | 'Arbitrum' | 'Avalanche' | 'SolanaDevnet' | 'Polkadot' | 'PolkadotTestnet' | 'PolkadotAssetHub' | 'PolkadotAssetHubTestnet'

export interface IManagedPaymentMethodDetails {
  paymentId: string
  paymentAddress: string
  network: INetwork
  token?: string
  amount: string
  expiresAt: string
}

export interface IManagedTokenDelegatePaymentMethodDetails {
  paymentId: string
  messageToSign: string
  network: INetwork
  token: string
  amount: string
}

export interface IPaymentLinkOrder {
  id: string
  paymentLinkId: string
  paymentMethodDetails: PaymentMethodDetails
  orderTotal: number
  createdAt: string
}

export interface IDonationLinkOrder {
  id: string
  donationLinkId: string
  paymentMethodDetails: PaymentMethodDetails
  orderTotal: number
  createdAt: string
}

export interface SubscriptionPaymentRes {
  id: string
  paymentMethodDetails: PaymentMethodDetails
  createdAt: string
}

export interface PaymentMethodDetails {
  managed?: IManagedPaymentMethodDetails
  managedTokenDelegate?: IManagedTokenDelegatePaymentMethodDetails
}

export interface NewPendingInvoicePayment {
  invoiceId: string
  paymentMethod: {
    managed?: {
      method: IManagedPaymentMethod
    }
  }
}

export interface PendingInvoicePaymentResponse {
  id: string
  invoiceId: string
  paymentMethodDetails: {
    managed?: IManagedPaymentMethodDetails
  }
  createdAt: Date
  orderTotal: number
}

export interface PaymentTransaction {
  amount: string
  transactionHash: string
  network: INetwork
  token?: string
}

export interface IManagedPaymentStatus {
  status: 'pending' | 'paymentDetected' | 'paymentConfirmed' | 'inReview' | 'expired' | 'failed'
  amountRemaining: string
  transactions: PaymentTransaction[]
  pendingTransactions: PaymentTransaction[]
  expiresAt?: string
}

export interface ICompleteTokenDelegatePaymentRes {
  status: 'Pending' | 'InReview' | 'Failed' | 'Completed'
}

export type CheckoutFunnelSourceType = 'checkoutSession' | 'paymentLink' | 'invoice' | 'donationLink' | 'presale'
export type CheckoutFunnelRail = 'crypto' | 'openBanking'
export type CheckoutFunnelStep =
  | 'viewed'
  | 'travelRuleCompleted'
  | 'paymentInitiated'
  | 'bankRedirectReturned'
  | 'paymentConfirmed'
  | 'paymentFailed'

export interface CheckoutFunnelEvent {
  sourceType: CheckoutFunnelSourceType
  sourceId: string
  rail: CheckoutFunnelRail
  step: CheckoutFunnelStep
  sessionKey: string
  metadata?: Record<string, unknown>
}

export type ManagedSubscriptionPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annually' | 'custom'

export interface SubscriptionPayment {
  date: string
  network: INetwork
  token?: string
  amount: string
  transactionHashes: string[]
}

export interface Subscription {
  id: string
  organizationId: string
  associatedEntityType: 'paymentLinkOrder' | 'checkoutSession' | 'invoice' | 'paymentSession'
  associatedEntityId: string
  subscriptionType: {
    automatedEVMSubscription?: {
      buyerAddress: string
      subscriptionContractAddress: string
      recommendedAllowanceDuration?: number
    }
    emailInvoiceSubscription?: {
      emailAddress: string
    }
  }
  status: 'active' | 'cancelled' | 'expired'
  network: string
  token?: string
  amount: string
  currency: string
  period: ManagedSubscriptionPeriod
  periodCustomDuration?: number
  tags?: { [key: string]: string }
  nextBillingDateAt: string
  createdAt: string
  product?: IProduct
  customizations?: CheckoutCustomizations
  payments: SubscriptionPayment[]
}

export interface RedeemDiscount {
  paymentLinkId?: string
  checkoutSessionId?: string
  code: string
}
export interface IDonationLink {
  id: string
  url: string
  name: string
  currency: string
  createdAt: Date
  updatedAt: Date
  primaryButtonColor: string
  inputFields: InputField[]
  methods: IManagedPaymentMethod[]
  imageUrl?: string
  bannerImageUrl?: string
  tosUrl?: string
  customizations?: {
    enableKycCheck: boolean
    allowedCountries: string[]
    warningMessage: string
  }
}

export interface Customer {
  id: string
  name: string
  address: string
  dateOfBirth: string
  billingAddress?: string
  email?: string
  residenceCountry?: string
  phone?: string
  dob?: string
  passportFile?: string
  idCardFile?: string
  selfieFile?: string
}

export type ICreateCustomer = Omit<Customer, 'id'>

const extractApiErrorMessage = (err: any, fallback = 'Unknown error'): string => {
  if (err?.response?.status === 429) {
    return 'Too many requests right now. Please wait a few seconds and try again.'
  }

  return err?.response?.data?.error ?? err?.response?.data?.message ?? err?.message ?? fallback
}

class Radom {
  private readonly inFlightGetRequests = new Map<string, Promise<unknown>>()

  private readonly dedupeGet = async <T>(cacheKey: string, fetcher: () => Promise<T>): Promise<T> => {
    const existingRequest = this.inFlightGetRequests.get(cacheKey) as Promise<T> | undefined
    if (existingRequest) {
      return await existingRequest
    }

    const request = fetcher().finally(() => {
      this.inFlightGetRequests.delete(cacheKey)
    })
    this.inFlightGetRequests.set(cacheKey, request)

    return await request
  }

  getPaymentLink = async (id: string): Promise<IPaymentLink> => {
    return await this.dedupeGet(`payment_link:${id}`, async () =>
      await axios.get(`${API_BASE_URL}/payment_link/${id}`)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    )
  }

  getDonationLink = async (id: string): Promise<IDonationLink> => {
    return await this.dedupeGet(`donation_link:${id}`, async () =>
      await axios.get(`${API_BASE_URL}/donation_link/${id}`)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    )
  }

  getInvoice = async (id: string): Promise<IInvoice> => {
    return await this.dedupeGet(`invoice:${id}`, async () =>
      await axios.get(`${API_BASE_URL}/invoice/${id}`)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    )
  }

  redeemDiscountCode = async (req: RedeemDiscount): Promise<IDiscountCode> => {
    return await axios.post(`${API_BASE_URL}/discount_code/verify`, req).then(res => res.data)
  }

  getPriceQuotes = async (from: string[], to: string[]): Promise<PriceQuote[]> => {
    const fromCurrencies = from.map((f, i) => `from[${i}]=${f}`).join('&')
    const toCurrencies = to.map((f, i) => `to[${i}]=${f}`).join('&')
    return await axios.get(`${API_BASE_URL}/price_quotes?${fromCurrencies}&${toCurrencies}`).then(res => res.data)
  }

  getCountryGuess = async (): Promise<CountryGuessResponse | null> => {
    try {
      return await this.dedupeGet('country_guess', async () =>
        await axios.get(`${API_BASE_URL}/country_guess`).then(res => res.data)
      ) as CountryGuessResponse
    } catch (err: any) {
      console.warn('Failed to guess country', err?.response?.data ?? err?.message ?? err)
      return null
    }
  }

  getCheckoutSession =
    async (id: string, options: GetCheckoutSessionOptions = {}): Promise<CheckoutSession> => {
      const query = options.refreshOpenBankingStatus ? '?refreshOpenBankingStatus=true' : ''
      const cacheKey = `checkout_session:${id}:${options.refreshOpenBankingStatus === true ? 'refresh' : 'default'}`

      const checkoutSession = await this.dedupeGet(cacheKey, async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/checkout_session/${id}${query}`)
          return res.data
        } catch (err) {
          throw new Error(extractApiErrorMessage(err))
        }
      })
      return checkoutSession
    }

  trackCheckoutFunnelEvent = async (event: CheckoutFunnelEvent): Promise<void> => {
    try {
      await axios.post(`${API_BASE_URL}/checkout/funnel/event`, event)
    } catch (err: any) {
      console.warn('Failed to track checkout funnel event', err?.response?.data ?? err?.message ?? err)
    }
  }

  upsertTrfsForCheckoutSession =
    async (id: string, trFields: TravelRuleFields): Promise<CheckoutSession> => {
      return await axios.put(`${API_BASE_URL}/checkout_session/${id}/travel_rule_fields`, trFields).then(res => res.data)
    }

  updateTrfsForCustomerViaInvoice =
    async (id: string, trFields: TravelRuleFields): Promise<void> => {
      return await axios.put(`${API_BASE_URL}/invoice/${id}/travel_rule_fields`, trFields)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    }

  getSellerProfile = async (address: string): Promise<SellerProfile> => {
    return await this.dedupeGet(`seller:${address}`, async () =>
      await axios.get(`${API_BASE_URL}/sellers/${address}`)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    )
  }

  createPaymentLinkOrder = async (data: NewPendingPaymentLinkOrder): Promise<IPaymentLinkOrder> => {
    return await axios.post(`${API_BASE_URL}/payment_link_order/pending`, data)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  createDonationLinkOrder = async (data: NewPendingDonationLinkOrder): Promise<IDonationLinkOrder> => {
    return await axios.post(`${API_BASE_URL}/donation_link_order/pending`, data)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  createCheckoutSessionOrder = async (data: NewPendingCheckoutSessionOrder): Promise<ICheckoutSessionOrder> => {
    return await axios.post(`${API_BASE_URL}/checkout/pending`, data)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  createInvoicePayment = async (data: NewPendingInvoicePayment): Promise<PendingInvoicePaymentResponse> => {
    return await axios.post(`${API_BASE_URL}/invoices/pending`, data)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  getManagedPaymentStatus = async (paymentId: string): Promise<IManagedPaymentStatus> => {
    return await axios.get(`${API_BASE_URL}/managed_payment_status/${paymentId}`).then(res => res.data)
  }

  getManagedTokenPaymentStatus = async (paymentId: string): Promise<IManagedPaymentStatus> => {
    return await axios.get(`${API_BASE_URL}/managed_token_payment_status/${paymentId}`).then(res => res.data)
  }

  completeTokenDelegatePayment = async (
    paymentId: string,
    signature: string
  ): Promise<ICompleteTokenDelegatePaymentRes> => {
    return await axios.post(`${API_BASE_URL}/token_delegate_payment`, { paymentId, signature })
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  getSubscription = async (id: string): Promise<Subscription> => {
    return await this.dedupeGet(`subscription:${id}`, async () =>
      await axios.get(`${API_BASE_URL}/subscription/${id}`)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    )
  }

  getSubscriptionBackButtonUrl = async (id: string): Promise<string | null> => {
    return await this.dedupeGet(`subscription_back_button_url:${id}`, async () =>
      await axios.get(`${API_BASE_URL}/subscription/${id}/subscription_back_button_url`)
        .then(res => res.data)
        .catch(err => { throw new Error(extractApiErrorMessage(err)) })
    )
  }

  estimateTokenTranferGas = async (network: string, token: string): Promise<{ gasCost: string }> => {
    return await axios.get(`${API_BASE_URL}/estimate_token_transfer_gas?network=${network}&token=${token}`)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  createSubscriptionPayment = async (subscriptionId: string): Promise<SubscriptionPaymentRes> => {
    return await axios.post(`${API_BASE_URL}/subscription/${subscriptionId}/pay`)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  cancelSubscription = async (id: string): Promise<void> => {
    return await axios.post(`${API_BASE_URL}/subscription/${id}/cancel`)
      .then(res => res.data)
      .catch(err => { throw new Error(extractApiErrorMessage(err)) })
  }

  updateCustomerKycData = async (id: string, customerForm: ICreateCustomer): Promise<void> => {
    const data = {
      ...customerForm
    }
    return await axios.put(`${API_BASE_URL}/checkout/${id}/kyc`, data)
      .then(res => res.data)
      .catch(err => {
        console.error('Error updating customer KYC data:', err.response?.data || err.message)
        throw new Error(extractApiErrorMessage(err))
      })
  }

  private readonly postForm = async<T>(path: string, body: any = {}): Promise<T> => {
    return await axios.postForm(`${API_BASE_URL}${path}`, body, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    }).then(res => res.data)
  }

  getGoolgePlaceAutoComplete = async (
    input: string,
    regionCode: string
  ): Promise<string[]> => {
    if (!input) {
      return []
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/address_suggestion`, {
        params: {
          input,
          includedRegionCodes: regionCode
        }
      })

      return (Array.isArray(response.data) ? response.data : [])
        .map(suggestion => {
          if (typeof suggestion === 'string') {
            return suggestion
          }

          if (typeof suggestion?.description === 'string') {
            return suggestion.description
          }

          if (typeof suggestion?.formattedAddress === 'string') {
            return suggestion.formattedAddress
          }

          if (typeof suggestion?.text === 'string') {
            return suggestion.text
          }

          if (typeof suggestion?.text?.text === 'string') {
            return suggestion.text.text
          }

          if (typeof suggestion?.placePrediction?.text?.text === 'string') {
            return suggestion.placePrediction.text.text
          }

          return ''
        })
        .filter((suggestion): suggestion is string => suggestion.trim().length > 0)
    } catch (err: any) {
      console.error('Error fetching address suggestions:', err.response?.data || err.message)
      return []
    }
  }

  getGooglePlaceAddressDetails = async (
    input: string,
    regionCode?: string
  ): Promise<AddressSuggestionDetails | null> => {
    if (!input || input.trim().length === 0) {
      return null
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/address_suggestion/details`, {
        params: {
          input,
          includedRegionCodes: regionCode ?? ''
        }
      })

      return response.data
    } catch (err: any) {
      console.error('Error fetching address details:', err.response?.data || err.message)
      return null
    }
  }

  getCustomerMatchEmailDobByDonationLink = async (
    donationLinkId: string,
    email: string,
    dob?: string
  ): Promise<any> => {
    const params: Record<string, string> = { email }
    if (dob) params.dob = dob

    try {
      const res = await axios.get(`${API_BASE_URL}/donation_link/${donationLinkId}/customer`, {
        params
      })
      return res.data
    } catch (err: any) {
      throw new Error(extractApiErrorMessage(err))
    }
  }

  createCustomerForDonationLink = async (
    donationLinkId: string,
    customer: ICreateCustomer
  ): Promise<any> => {
    try {
      const res = await axios.put(`${API_BASE_URL}/donation_link/${donationLinkId}/customer/kyc`, customer)
      return res.data
    } catch (err: any) {
      throw new Error(extractApiErrorMessage(err))
    }
  }

  updateCustomerKycDataByDonationLink = async (
    donationLinkId: string,
    customerEmail: string,
    customerDob: string,
    updateKycData: KycData
  ): Promise<void> => {
    try {
      const res = await axios.put(`${API_BASE_URL}/donation_link/${donationLinkId}/customer/kyc`, {
        customerEmail,
        customerDob,
        updateKycData
      })
      return res.data
    } catch (err: any) {
      throw new Error(extractApiErrorMessage(err))
    }
  }

  submitCustomerKycCheckRequestByDonationLink = async (
    donationLinkId: string,
    customerEmail: string,
    customerDob: string
  ): Promise<any> => {
    try {
      const res = await axios.put(`${API_BASE_URL}/donation_link/${donationLinkId}/customer/kyc`, {
        customerEmail,
        customerDob
      })
      return res.data
    } catch (err: any) {
      throw new Error(extractApiErrorMessage(err))
    }
  }
}

export default new Radom()
